import Dialog from "../../General/ConfirmableDialog";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Stack,
  Typography,
  Chip,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Dayjs } from "dayjs";
import useAuth from "../../../hooks/useAuth";
import { LogbookCheck } from "../../../interfaces/Fleet/ILogbookCheck";
import {
  createLogbookCheck,
  updateLogbookCheck,
} from "../../../api/logbookCheckService";

interface Props {
  open: boolean;
  onClose: () => void;
  driverId: string;
  driverName: string;
  dates: Dayjs[];
  existing: (LogbookCheck | null)[];
  onSaved: (check: LogbookCheck | LogbookCheck[]) => void;
}

const fields = [
  { name: "isCertified", label: "Certified" },
  { name: "isShippingIdAdded", label: "Shipping ID Added" },
  { name: "isTrailerIdAdded", label: "Trailer ID Added" },
  { name: "isDayOff", label: "Day Off" },
  { name: "isOverPersonalUsed", label: "Over Personal Use" },
  { name: "hasViolation", label: "Has Violation" },
] as const;

type FlagName = (typeof fields)[number]["name"];

const createFlagState = (): Record<FlagName, boolean> => ({
  isCertified: false,
  isShippingIdAdded: false,
  isTrailerIdAdded: false,
  isDayOff: false,
  isOverPersonalUsed: false,
  hasViolation: false,
});

const LogbookCheckDialog = ({
  open,
  onClose,
  driverId,
  driverName,
  dates,
  existing,
  onSaved,
}: Props) => {
  const { token } = useAuth();
  const [flags, setFlags] =
    useState<Record<FlagName, boolean>>(createFlagState);
  const [indeterminateFlags, setIndeterminateFlags] =
    useState<Record<FlagName, boolean>>(createFlagState);
  const [dirtyFlags, setDirtyFlags] =
    useState<Record<FlagName, boolean>>(createFlagState);

  useEffect(() => {
    const nextFlags = createFlagState();
    const nextIndeterminate = createFlagState();

    if (existing.length > 0) {
      fields.forEach((field) => {
        const values = existing.map((item) => !!item?.[field.name]);
        const allTrue = values.length > 0 && values.every(Boolean);
        const allFalse = values.every((value) => !value);

        nextFlags[field.name] = allTrue;
        nextIndeterminate[field.name] =
          !allTrue && !allFalse && values.length > 0;
      });
    }

    setFlags(nextFlags);
    setIndeterminateFlags(nextIndeterminate);
    setDirtyFlags(createFlagState());
  }, [existing, open]);

  const handleChange =
    (field: FlagName) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setFlags((prev) => ({ ...prev, [field]: checked }));
      setIndeterminateFlags((prev) => ({ ...prev, [field]: false }));
      setDirtyFlags((prev) => ({ ...prev, [field]: true }));
    };

  const handleSave = async () => {
    if (dates.length === 0) {
      onClose();
      return;
    }

    try {
      const results = await Promise.all(
        dates.map(async (date, index) => {
          const currentExisting = existing[index] || null;
          const payload: Partial<LogbookCheck> = {
            date: date.startOf("day").toISOString(),
            driverId,
          };

          fields.forEach((field) => {
            if (
              currentExisting &&
              !dirtyFlags[field.name] &&
              indeterminateFlags[field.name]
            ) {
              payload[field.name] = !!currentExisting[field.name];
            } else {
              payload[field.name] = flags[field.name];
            }
          });

          if (currentExisting) {
            return updateLogbookCheck(token, currentExisting.id, {
              ...payload,
              id: currentExisting.id,
            });
          }

          return createLogbookCheck(token, payload);
        })
      );

      onSaved(results.length === 1 ? results[0] : results);
      onClose();
    } catch (e) {
      console.error(e);
    }
  };

  const isMulti = dates.length > 1;
  const formattedDates = useMemo(
    () => dates.map((date) => date.format("MMM D, YYYY")),
    [dates]
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Logbook Check</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2" color="text.primary">
              {driverName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isMulti
                ? `${formattedDates.length} days selected`
                : formattedDates[0] || ""}
            </Typography>
            {isMulti && (
              <Stack
                direction="row"
                spacing={0.75}
                flexWrap="wrap"
                rowGap={0.75}
              >
                {formattedDates.map((label) => (
                  <Chip key={label} label={label} size="small" />
                ))}
              </Stack>
            )}
          </Stack>
          <Stack spacing={1.5}>
            {fields.map((f) => (
              <FormControlLabel
                key={f.name}
                control={
                  <Checkbox
                    checked={flags[f.name] || false}
                    indeterminate={
                      indeterminateFlags[f.name] && !dirtyFlags[f.name]
                    }
                    onChange={handleChange(f.name)}
                  />
                }
                label={f.label}
              />
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LogbookCheckDialog;
